// Shared date/time formatting for screens that display stored timestamps.
//
// TWO RULES, applied everywhere in the portal UI:
//
//   1. Dates are ALWAYS yyyy-mm-dd. Never dd/mm/yyyy or mm/dd/yyyy — those two are indistinguishable
//      for the first twelve days of a month, and this platform is used across Europe, the US and the
//      Middle East, so a reader cannot know which convention a screen is using. yyyy-mm-dd is
//      unambiguous, sorts lexicographically, and matches how the values are stored.
//   2. Times ALWAYS carry their timezone. Every timestamp is stored as UTC (`new Date().toISOString()`
//      server-side) but `toLocaleString()` renders in the VIEWER's zone with nothing to say so: a run
//      stored at 10:14:50Z reads as 11:14:50 in Dublin and 06:14:50 in New York.
//
// The `sv-SE` locale is used because its native format IS the ISO ordering (yyyy-mm-dd, 24-hour) — the
// alternative is assembling the string from Intl.DateTimeFormat parts, which then has to re-implement
// timezone-name resolution by hand. It is a formatting choice, not a language choice; nothing here is
// user-facing Swedish.
//
// Note dateStyle/timeStyle cannot be combined with timeZoneName under ECMA-402 (it throws), which is
// why the component options are spelled out.

const ISO_LOCALE = 'sv-SE';

const DATE_TIME_OPTIONS = {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
};

const DATE_ONLY_OPTIONS = { year: 'numeric', month: '2-digit', day: '2-digit' };

/**
 * Date + time in the viewer's local timezone, labelled with that zone.
 * e.g. "2026-08-11 11:14:50 GMT+1"
 * Returns the placeholder for null/undefined/unparseable input rather than "Invalid Date".
 */
export const formatDateTime = (value, placeholder = '—') => {
    if (!value) return placeholder;
    const d = new Date(value);
    if (isNaN(d)) return placeholder;
    return d.toLocaleString(ISO_LOCALE, DATE_TIME_OPTIONS);
};

/**
 * The same instant in UTC, explicitly labelled — for anywhere the stored value itself matters more
 * than the viewer's wall clock (support, comparing against CloudWatch or an OHIP timestamp).
 * e.g. "2026-08-11 10:14:50 UTC"
 */
export const formatDateTimeUtc = (value, placeholder = '—') => {
    if (!value) return placeholder;
    const d = new Date(value);
    if (isNaN(d)) return placeholder;
    return `${d.toLocaleString(ISO_LOCALE, { ...DATE_TIME_OPTIONS, timeZone: 'UTC', timeZoneName: undefined })} UTC`;
};

/**
 * Date only — "2026-08-11". No timezone label, because a bare date has no instant to be ambiguous
 * about. Still derived from the LOCAL date, so it agrees with what formatDateTime shows.
 */
export const formatDate = (value, placeholder = '—') => {
    if (!value) return placeholder;
    // A BARE DATE ('2026-04-01' — a billing period's start, a DATE column) is already yyyy-mm-dd and has no instant:
    // `new Date()` would read it as midnight UTC and the viewer's zone could show the day before.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (isNaN(d)) return placeholder;
    return d.toLocaleDateString(ISO_LOCALE, DATE_ONLY_OPTIONS);
};

/**
 * The local date for a stored timestamp, as a yyyy-mm-dd key — for grouping rows by day.
 *
 * Use this rather than `iso.slice(0, 10)`: slicing the raw ISO string yields the UTC date, which
 * disagrees with the local time shown in the row for anything between 23:00 and midnight UTC (a run at
 * 2026-08-11T23:30Z would file under 2026-08-11 while displaying as 2026-08-12 00:30).
 */
export const dateKey = (value) => {
    if (!value) return 'unknown';
    const d = new Date(value);
    if (isNaN(d)) return 'unknown';
    return d.toLocaleDateString(ISO_LOCALE, DATE_ONLY_OPTIONS);
};

/**
 * Both renderings in one string, for a `title` tooltip: local (labelled) and the stored UTC value.
 * Lets someone reading a screen check a timestamp against a log without converting in their head.
 */
export const formatDateTimeBoth = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d)) return '';
    return `${formatDateTime(value)}  •  ${formatDateTimeUtc(value)}`;
};
